/**
 * azure-functions/shared/workflows/runAssurance.cjs
 *
 * The achieved evidence strength of a workflow run and the ONE countability
 * rule (docs/workflow-engine-design.md §3e "One countability rule", §3g).
 * One copy, shared by the browser and the server: the runner stamps
 * achievedStrength with it at completion, and src/utils/workflowDefinition.js
 * re-exports isRunCountable, so the obligation criterion, gate 4 and the
 * control view (PDWB-02) can never disagree about whether an activity happened
 * with adequate evidence.
 *
 * Nothing here says a control is compliant or effective (WF-B-005,
 * TRYGGR_INTENT.md §6.2): a countable run is "the activity happened, inside its
 * window, with at least the evidence its definition expects".
 */

'use strict';

const { STRENGTHS, ACTION_BY_ID, strengthRank, isNegativeOutcome } = require('./actionCatalogue.cjs');
const { definitionScopes } = require('./validateDefinition.cjs');

const MEDIUM = strengthRank('Medium');
const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

/**
 * The strongest evidence the run's pass actually rests on (design §3g): only
 * steps on the path that reached the run's End — inside a parallel group,
 * only joining children that ended `pass`. Never counted: a negative outcome,
 * a step cancelled by `join-resolved` (or any cancelled step), a dead end, a
 * failed group's children. A `selfReviewed` step was recorded at Low and
 * counts at Low. A run that did not complete `pass` has no achieved strength.
 *
 * @param {{ run: object, stepInstances: object[] }} entry  run.definitionSnapshot is the frozen version
 * @returns {string|null} a STRENGTHS value, or null
 */
function achievedStrength({ run, stepInstances }) {
  const steps = passingEvidenceSteps({ run, stepInstances });
  if (steps === null) return null;
  const best = Math.max(-1, ...steps.map((inst) => strengthRank(inst.result.strength)));
  return best >= 0 ? STRENGTHS[best] : 'None';
}

/**
 * True when a step the run's pass rests on was self-reviewed (§3b.6) — no
 * independent person existed, so it was recorded at Low. Stamped on the run
 * (run.selfReviewed) so a run-level figure never hides the missing four eyes
 * behind a stronger step elsewhere on the path (PDWA-04 co-owner).
 */
function passRestsOnSelfReview({ run, stepInstances }) {
  const steps = passingEvidenceSteps({ run, stepInstances });
  return Array.isArray(steps) && steps.some((inst) => inst.selfReviewed === true);
}

/** The evidence-bearing step instances on the path that reached a pass End, or null. */
function passingEvidenceSteps({ run, stepInstances }) {
  if (!isObj(run) || run.status !== 'completed' || run.outcome !== 'pass') return null;
  const snapshot = isObj(run.definitionSnapshot) ? run.definitionSnapshot : null;
  if (!snapshot || !Array.isArray(snapshot.steps)) return null;
  const { scopeOf, scopes } = definitionScopes(snapshot);
  const byStep = new Map((stepInstances || []).filter(isObj).map((s) => [s.stepId, s]));
  const byId = new Map(snapshot.steps.filter(isObj).map((s) => [s.id, s]));
  const childScopesOf = (groupId) => scopes.filter((sc) => sc.group === groupId);

  const out = [];
  const visit = (scopeId) => {
    for (const [stepId, sc] of scopeOf) {
      if (sc !== scopeId) continue;
      const def = byId.get(stepId);
      const inst = byStep.get(stepId);
      if (!def || !inst || inst.activation !== 'activated' || inst.status !== 'completed') continue;
      if (def.action === 'parallel-group') {
        if (inst.outcome !== 'pass') continue;
        const children = Array.isArray(inst.children) ? inst.children : [];
        childScopesOf(stepId).forEach((child, j) => {
          const c = children[j];
          if (c && !c.deadEnd && c.result === 'pass') visit(child.id);
        });
        continue;
      }
      const spec = ACTION_BY_ID[def.action];
      if (!spec || spec.strength === null || spec.strength === 'None') continue;
      if (inst.outcome && isNegativeOutcome(def.action, inst.outcome)) continue;
      const recorded = isObj(inst.result) && STRENGTHS.includes(inst.result.strength) ? inst.result.strength : null;
      if (!recorded) continue; // no evidence (e.g. a Fetch that was not obtained)
      out.push(inst);
    }
  };
  visit('main');
  return out;
}

/**
 * THE countability rule (design §3e). A run is countable when it is
 * `completed`, outcome `pass`, not resting on self-review (D11 = A, below),
 * completed inside its cadence window, with
 * achieved ≥ expected (the snapshot's expectedStrength — never the
 * minimumPassStrength floor, which would lower the bar) and achieved ≥ Medium.
 *
 * @param {object} run  as stored: status, outcome, completedAt, achievedStrength, definitionSnapshot
 * @param {{ now: Date|string|number, windowMs: number }} window  the cadence window ending at `now`
 * @returns {boolean}
 */
function isRunCountable(run, { now, windowMs } = {}) {
  if (!isObj(run) || run.status !== 'completed' || run.outcome !== 'pass') return false;
  // Founder decision D11 = A (2026-10-06; TRYGGR_INTENT.md §5 fail-safe): a
  // pass that rests on a self-reviewed step — no independent person existed
  // (§3b.6) — is NOT countable, for gate 4, the obligation criterion and a
  // risk's workflow backing alike.
  if (run.selfReviewed === true) return false;
  const achieved = strengthRank(run.achievedStrength);
  const expected = strengthRank(isObj(run.definitionSnapshot) ? run.definitionSnapshot.expectedStrength : null);
  if (achieved < 0 || expected < 0) return false;
  if (achieved < expected || achieved < MEDIUM) return false;
  const at = Date.parse(run.completedAt);
  const end = now instanceof Date ? now.getTime() : typeof now === 'number' ? now : Date.parse(now);
  if (!Number.isFinite(at) || !Number.isFinite(end) || !(Number.isFinite(windowMs) && windowMs > 0)) return false;
  return at <= end && at > end - windowMs;
}

/** True when a completed pass rests on less than its definition expects (design §3g wording). */
function isBelowExpected(run) {
  if (!isObj(run) || run.status !== 'completed' || run.outcome !== 'pass') return false;
  const achieved = strengthRank(run.achievedStrength);
  const expected = strengthRank(isObj(run.definitionSnapshot) ? run.definitionSnapshot.expectedStrength : null);
  return achieved >= 0 && expected >= 0 && achieved < expected;
}

// ── Cadence (design §3e) ─────────────────────────────────────────────────────
// Moved here from runner.js in PDWB-02 so the control view reads a run's
// cadence window from the same copy the runner schedules with.

const DAY_MS = 24 * 60 * 60 * 1000;
const PERIOD_MONTHS = { '1m': 1, '3m': 3, '6m': 6, '1y': 12 };

/**
 * The frequency a scheduled definition runs on (design §3e): its own fixed
 * frequency, or — when it borrows the obligation cadence — the SHORTEST
 * effective cadence among its linked obligations (a tenant's cadenceOverride
 * wins over the catalogue's), so no linked obligation is left without a run
 * inside its window. Null when nothing recurs ('once' only, or no link resolves).
 *
 * @param {object} snapshot  a definition version
 * @param {{ enabledObligations: object[], obligationsById: Map<string, object> }} ctx
 */
function scheduleFrequency(snapshot, { enabledObligations, obligationsById }) {
  const t = snapshot && snapshot.trigger;
  if (!t || t.type !== 'schedule') return null;
  if (t.frequency) return PERIOD_MONTHS[t.frequency] ? t.frequency : null;
  const linked = (snapshot.links && snapshot.links.obligations) || [];
  const enabled = new Map((enabledObligations || []).filter((e) => e && e.obligationId).map((e) => [e.obligationId, e]));
  const freqs = linked
    .map((id) => {
      const override = enabled.get(id) && enabled.get(id).cadenceOverride && enabled.get(id).cadenceOverride.frequency;
      const def = obligationsById.get(id);
      return override || (def && def.cadence && def.cadence.frequency) || null;
    })
    .filter((f) => PERIOD_MONTHS[f]);
  if (!freqs.length) return null;
  return freqs.sort((a, b) => PERIOD_MONTHS[a] - PERIOD_MONTHS[b])[0];
}

/** The cadence window a run of this definition is countable in (isRunCountable's windowMs). */
function cadenceWindowMs(frequency) {
  return PERIOD_MONTHS[frequency] ? Math.round(PERIOD_MONTHS[frequency] * 30.44 * DAY_MS) : null;
}

/**
 * THE overdue rule (PDWB-02; co-owner finding 8 — one definition for the
 * control view, the management-review input and the planned registry metric):
 * a scheduled definition is overdue when it has been live for at least one
 * cadence window and no run of it is countable inside the window ending now.
 * A failed, below-expected or cancelled run never keeps a definition current.
 * No cadence (a manual or once-only definition) → never overdue.
 *
 * @param {{ runs: object[], firstPublishedAt: string|null, now: Date|string|number, windowMs: number|null }} input
 */
function isDefinitionOverdue({ runs, firstPublishedAt, now, windowMs }) {
  if (!(Number.isFinite(windowMs) && windowMs > 0)) return false;
  const end = now instanceof Date ? now.getTime() : typeof now === 'number' ? now : Date.parse(now);
  const live = Date.parse(firstPublishedAt);
  if (!Number.isFinite(end)) return false;
  // Live for less than one window: its first window has not ended yet.
  if (Number.isFinite(live) && live > end - windowMs) return false;
  return !(runs || []).some((r) => isRunCountable(r, { now: end, windowMs }));
}

module.exports = {
  achievedStrength,
  isDefinitionOverdue,
  passRestsOnSelfReview,
  passingEvidenceSteps,
  isRunCountable,
  isBelowExpected,
  PERIOD_MONTHS,
  scheduleFrequency,
  cadenceWindowMs,
};
